import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import {
  EmperorDialogRefService,
  EmperorDialogSize,
  EmperorUIButtonTypes,
} from '@boldpenguin/emperor-presentational';
import { AppointmentTypes } from '@boldpenguin/sdk';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select } from '@ngrx/store';
import {
  combineLatest,
  filter,
  Observable,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { IEnrollment } from 'src/app/core/models/enrollments.model';
import { ComplianceFormService } from 'src/app/core/services/compliance-form/compliance-form.service';
import {
  IRouteData,
  PermissionsService,
} from 'src/app/core/services/permissions/permissions.service';
import { AppointmentEnrollmentPermissions } from 'src/app/permissions/permissions';
import { AppointmentAction } from '../../models/appointment-dialog.model';
import {
  getIncompleteCarrierQuestionGroups,
  isMainComplianceFormComplete,
} from '../../utilities/compliance-form-utilities';
import { AppointmentDialogComponent } from '../appointment-dialog/appointment-dialog.component';

@UntilDestroy()
@Component({
  selector: 'app-carrier-appointment-logo-buttons',
  templateUrl: './carrier-appointment-logo-buttons.component.html',
  styleUrls: ['./carrier-appointment-logo-buttons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CarrierAppointmentLogoButtonsComponent implements OnInit {
  @Input() carriers: IEnrollment[];
  @Input() appointmentAction: AppointmentAction;

  isComplianceFormComplete$: Observable<boolean>;
  incompleteQuestionGroups$: Observable<string[]>;
  hasPermissionToView$: Observable<boolean>;

  isDialogOpen = false;

  emperorDialogRefService = inject(EmperorDialogRefService);
  complianceFormService = inject(ComplianceFormService);
  permissionsService = inject(PermissionsService);

  readonly EmperorUIButtonTypes = EmperorUIButtonTypes;

  ngOnInit() {
    this.setUpDialog();
  }

  openAppointmentDialog(enrollment: IEnrollment) {
    combineLatest([
      this.isComplianceFormComplete$,
      this.incompleteQuestionGroups$,
    ])
      .pipe(
        filter(() => !this.isDialogOpen),
        tap(() => {
          this.isDialogOpen = true;
        }),
        switchMap(
          ([isComplianceFormComplete, incompleteQuestionGroups]) =>
            this.emperorDialogRefService.open(AppointmentDialogComponent, {
              data: {
                enrollment,
                isFTW: false,
                isDirect:
                  enrollment.appointment_type === AppointmentTypes.Direct,
                type: this.appointmentAction,
                isComplianceFormComplete,
                incompleteQuestionGroups,
              },
              backdropDismiss: true,
              uiDialogSize: EmperorDialogSize.Medium,
            }).afterClosed,
        ),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.isDialogOpen = false;
      });
  }

  private setUpDialog() {
    const answers$ = this.complianceFormService
      .getComplianceQuestionAnswers()
      .pipe(shareReplay({ bufferSize: 1, refCount: true }));

    this.incompleteQuestionGroups$ = answers$.pipe(
      select((complianceResponse) =>
        getIncompleteCarrierQuestionGroups(
          complianceResponse.completed_question_groups,
        ),
      ),
    );

    this.isComplianceFormComplete$ = answers$.pipe(
      select((complianceResponse) =>
        isMainComplianceFormComplete(
          complianceResponse.completed_question_groups,
        ),
      ),
    );

    this.hasPermissionToView$ = this.permissionsService.hasRoutePermission(
      AppointmentEnrollmentPermissions as IRouteData,
    );
  }
}
